// Sticky add to cart (snippets/block-sticky-add-to-cart.liquid). The bar shows after the main add to cart button
// scrolls away, after a scroll distance, or always. Its button submits the main buy buttons form (so Quantity breaks,
// subscriptions and the cart drawer behave as with the main button); its variant dropdown sets the main variant.
(() => {
  if (customElements.get('sticky-cart')) return;

  // Shared helper: assets/money.js (loaded in layout/theme.liquid).
  const formatMoney = (cents, format) => window.theme.formatMoney(cents, format);

  class StickyCart extends HTMLElement {
    connectedCallback() {
      // Add block picture (snippets/visual-preview): the bar is shown in place, as a picture.
      if (document.documentElement.dataset.visualPreview === 'true') { this.hidden = false; this.classList.add('is-visible'); return; }
      if (this.parentElement !== document.body) {
        // Fixed bars must not sit inside a transformed parent: move to <body>, replacing an older copy of this bar.
        this.page = this.closest('product-page');
        this.key = this.closest('[data-product-part]')?.dataset.productPart || '';
        this.dataset.key = this.key;
        document.querySelectorAll(`body > sticky-cart[data-key="${this.key}"]`).forEach((old) => old.remove());
        this.pageRef = this.page;
        document.body.append(this);
        return;
      }
      this.page = this.pageRef || document.querySelector('product-page');
      this.hidden = false;
      this.querySelector('[data-sticky-add]')?.addEventListener('click', () => this.add());
      this.querySelector('[data-sticky-variant]')?.addEventListener('change', (event) => this.setVariant(Number(event.target.value)));
      // The price follows the selected Quantity breaks tier.
      this.onOffer = (event) => this.offer(event.detail);
      this.page?.addEventListener('quantity-breaks:change', this.onOffer);
      const breaks = this.page?.querySelector('quantity-breaks');
      if (breaks?.offer) this.offer(breaks.offer);
      this.offers();
      // No Quantity breaks: the price of the purchase option (Subscribe & save per item, a Subscription plans pack).
      this.onSubscription = () => this.subscriptionPrice();
      this.page?.addEventListener('subscription:change', this.onSubscription);
      this.subscriptionPrice();
      // A bundle app's price (assets/product.js watchApps).
      this.onAppOffer = (event) => this.offer({ total: event.detail.price, compare: event.detail.compare });
      this.page?.addEventListener('product:offer', this.onAppOffer);
      this.onScroll = () => this.update();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      if (!this.onScroll) return;
      this.page?.removeEventListener('quantity-breaks:change', this.onOffer);
      this.page?.removeEventListener('subscription:change', this.onSubscription);
      this.page?.removeEventListener('product:offer', this.onAppOffer);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    // Offer dropdowns: Quantity breaks tiers and the subscription choices (one-time / subscribe / plan cards) of this page.
    offers() {
      const tierSelect = this.querySelector('[data-sticky-tier]');
      const planSelect = this.querySelector('[data-sticky-plan]');
      const format = this.dataset.moneyFormat || '{{amount}}';
      const tiers = [...(this.page?.querySelectorAll('quantity-breaks [data-qb-tier]') || [])].filter((tier) => tier.dataset.soldOut !== 'true' && (tier.checkVisibility?.() ?? true));
      if (tierSelect && tiers.length > 1) {
        // Tier name and the price it shows now (with the subscription price while subscribed).
        const label = (tier) => {
          const total = tier.querySelector('.qb-tier__prices')?.dataset.total;
          return `${tier.dataset.title}${total ? ` · ${formatMoney(total, format)}` : ''}`;
        };
        tierSelect.replaceChildren(...tiers.map((tier, i) => new Option(label(tier), i)));
        tierSelect.hidden = false;
        const sync = () => {
          tiers.forEach((tier, i) => { if (tierSelect.options[i]) tierSelect.options[i].textContent = label(tier); });
          tierSelect.value = String(Math.max(0, tiers.findIndex((tier) => tier.classList.contains('is-selected'))));
        };
        sync();
        this.page.addEventListener('quantity-breaks:change', sync);
        tierSelect.addEventListener('change', () => tiers[Number(tierSelect.value)]?.click());
      }
      // The first Subscribe & save / Subscription plans block of the page.
      const box = this.page?.querySelector('subscription-options');
      const choices = [...(box?.querySelectorAll('[data-sub-choice]') || [])].filter((input) => !input.closest('[hidden]'));
      if (planSelect && choices.length > 1) {
        planSelect.replaceChildren(...choices.map((input, i) => {
          const card = input.closest('[data-sub-option]');
          const name = input.getAttribute('aria-label') || (card?.querySelector('.sub-card__title') || card?.querySelector('.sub-card__head'))?.textContent.trim() || `Option ${i + 1}`;
          return new Option(name.replace(/\s+/g, ' '), i);
        }));
        planSelect.hidden = false;
        const sync = () => { planSelect.value = String(Math.max(0, choices.findIndex((input) => input.checked))); };
        sync();
        this.page.addEventListener('subscription:change', sync);
        planSelect.addEventListener('change', () => {
          const input = choices[Number(planSelect.value)];
          if (!input) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    }

    // Same price as the price block (assets/product.js offer).
    subscriptionPrice() {
      if (!this.page || this.page.querySelector('quantity-breaks') || !this.page.querySelector('subscription-options')) return;
      const offer = this.page.offer;
      if (offer) this.offer({ total: offer.price, compare: offer.compare });
    }

    offer(offer) {
      if (!offer) return;
      const format = this.dataset.moneyFormat || '{{amount}}';
      const price = this.querySelector('[data-sticky-price]');
      const compare = this.querySelector('[data-sticky-compare]');
      // with a subscription chosen: its price; plus the ticked Quantity breaks upsells (extra)
      const total = (offer.current || offer.total) + (offer.extra || 0);
      const was = (offer.compare || 0) + (offer.extraCompare || 0);
      if (price) price.textContent = formatMoney(total, format);
      if (compare) {
        compare.textContent = formatMoney(was, format);
        compare.hidden = !(was > total);
      }
      // The badge shows while there is a saving; "Save {percent}%" and the price in the button follow the offer.
      const badge = this.querySelector('.sticky-cart__badge');
      if (badge) {
        const saving = was > total ? Math.round(((was - total) * 100) / was) : 0;
        badge.hidden = saving <= 0;
        if (saving > 0 && badge.dataset.stickyBadge?.includes('{percent}')) badge.textContent = badge.dataset.stickyBadge.replace('{percent}', saving);
      }
      const buttonPrice = this.querySelector('.sticky-cart__button-price');
      if (buttonPrice) buttonPrice.textContent = formatMoney(total, format);
    }

    // The buy buttons form of the product (assets/product.js), not the price block's installments form.
    get form() {
      if (this.page?.buyForm) return this.page.buyForm;
      return [...(this.page?.querySelectorAll('form[action*="/cart/add"]') || [])].find((form) => !form.closest('cart-drawer, sticky-cart'));
    }

    update() {
      // Layout hook: are any dropdowns visible on this device? (snippets/block-sticky-add-to-cart.liquid)
      const selects = [...this.querySelectorAll('.sticky-cart__select')].some((select) => !select.hidden && getComputedStyle(select).display !== 'none');
      if (this.dataset.selects !== String(selects)) this.dataset.selects = String(selects);
      let show = true;
      if (this.dataset.trigger === 'scroll') show = window.scrollY > Number(this.dataset.distance || 0);
      if (this.dataset.trigger === 'after_button') {
        const button = this.form?.querySelector('[type="submit"]');
        show = button ? button.getBoundingClientRect().bottom < 0 : window.scrollY > 400;
      }
      if (window.Shopify?.designMode && this.dataset.trigger !== 'always') show = show || this.matches(':hover');
      this.classList.toggle('is-visible', show);
    }

    // The main form does the adding: bundles, selling plans, gifts and the cart drawer all apply. Pressing its button
    // (not only submitting the form) lets bundle apps that take over that click (Amose) add their bundle here too.
    add() {
      const form = this.form;
      if (!form) return;
      const submit = form.querySelector('[type="submit"]');
      if (submit?.disabled) return;
      if (submit) submit.click();
      else form.requestSubmit();
    }

    // Picks the variant in the main variant picker (or the form when there is none), which re-renders the product blocks.
    setVariant(id) {
      const variant = this.page?.variants?.find((v) => v.id === id);
      if (!variant || !this.page) return;
      const groups = this.page.querySelectorAll('[data-option-position]');
      if (groups.length) {
        groups.forEach((group) => {
          const value = variant.options[Number(group.dataset.optionPosition) - 1];
          const select = group.querySelector('select[data-option-input]');
          if (select) select.value = value;
          group.querySelectorAll('input[data-option-input]').forEach((input) => { input.checked = input.value === value; });
        });
        this.page.onOptionChange();
        return;
      }
      (this.page.buyForms || [this.page]).forEach((form) => form.querySelectorAll('input[name="id"], select[name="id"]').forEach((field) => { field.value = id; }));
      this.page.variant = variant;
      this.page.dispatch?.(variant);
      this.page.refresh?.(id);
    }
  }

  customElements.define('sticky-cart', StickyCart);
})();
